import React, { useState } from 'react';
import { fmt } from '../../../design/format';
import { ChartLegend, ChartTable, TipRow, TipTitle, allIntegers, nice, series, useChartTip, useChartWidth } from './core';

function topRounded(x, y, w, h, r) {
  if (h <= 0) return '';
  const rr = Math.min(r, w / 2, h);
  return `M${x} ${y + h}V${y + rr}Q${x} ${y} ${x + rr} ${y}H${x + w - rr}Q${x + w} ${y} ${x + w} ${y + rr}V${y + h}Z`;
}

/**
 * Vertical bars, grouped or stacked (mockup VL.charts.bars): 2px gaps, rounded data ends.
 * series: [{ name, values: number[], color?, slot? }]
 */
export function BarChart({
  labels,
  series: bars,
  stacked = false,
  height = 220,
  ml = 40,
  yMax,
  yFmt = fmt.k,
  vFmt = fmt.int,
  target,
  legend = true,
  tipLabels,
  xName = 'Category',
  aria = 'Bar chart',
  showTable = false,
}) {
  const [ref, W] = useChartWidth();
  const tip = useChartTip();
  const [hover, setHover] = useState(null);
  const colorOf = (s, si) => s.color || series(s.slot || si + 1);

  if (showTable) {
    return (
      <div className="chart" ref={ref}>
        <ChartTable head={[xName, ...bars.map((s) => s.name)]} rows={labels.map((l, i) => [l, ...bars.map((s) => vFmt(s.values[i]))])} />
      </div>
    );
  }

  const H = height;
  const m = { l: ml, r: 10, t: 10, b: 26 };
  const n = labels.length;
  const k = bars.length;
  const pw = W - m.l - m.r;
  const ph = H - m.t - m.b;
  const totals = labels.map((_, i) => (stacked ? bars.reduce((a, s) => a + s.values[i], 0) : Math.max(...bars.map((s) => s.values[i]))));
  const integer = !yMax && bars.every((s) => allIntegers(s.values));
  const sc = nice(yMax || Math.max(...totals, ...(target ? [target.value] : []), 0), 4, integer);
  const y = (v) => m.t + ph - (v / sc.max) * ph;
  const band = pw / n;
  const inner = Math.min(stacked ? 40 : 18 * k, band * 0.62);
  const bw = stacked ? inner : inner / k;
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(pw / 44))));

  const ticks = [];
  for (let v = 0; v <= sc.max + 1e-9; v += sc.step) ticks.push(v);

  const showTip = (i, ev) => {
    setHover(i);
    tip.show(
      <>
        <TipTitle>{tipLabels ? tipLabels[i] : labels[i]}</TipTitle>
        {[...bars].reverse().map((s) => {
          const si = bars.indexOf(s);
          return <TipRow key={s.name} color={colorOf(s, si)} label={s.name} value={vFmt(s.values[i])} />;
        })}
        {stacked && k > 1 && (
          <div className="tr" style={{ borderTop: '1px solid var(--divider)', marginTop: 4, paddingTop: 2 }}>
            <span>Total</span>
            <span className="v">{vFmt(totals[i])}</span>
          </div>
        )}
      </>,
      ev.clientX,
      ev.clientY
    );
  };

  return (
    <div className="chart" ref={ref}>
      {k > 1 && legend && <ChartLegend items={bars.map((s, si) => ({ name: s.name, color: colorOf(s, si) }))} />}
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={aria}>
        {ticks.map((v) => (
          <React.Fragment key={v}>
            <line className={v === 0 ? 'axis-line' : 'grid-line'} x1={m.l} x2={W - m.r} y1={y(v)} y2={y(v)} />
            <text className="tick" x={m.l - 8} y={y(v) + 4} textAnchor="end">
              {yFmt(v)}
            </text>
          </React.Fragment>
        ))}
        {labels.map((l, i) => {
          const cx = m.l + band * i + band / 2;
          const x0 = cx - inner / 2;
          let acc = 0;
          return (
            <g key={`${l}-${i}`}>
              {i % every === 0 && (
                <text className="tick" x={cx} y={H - 6} textAnchor="middle">
                  {l}
                </text>
              )}
              {bars.map((s, si) => {
                const val = s.values[i];
                let d;
                if (stacked) {
                  const top = y(acc + val);
                  const bot = y(acc);
                  const h = bot - top - (si ? 2 : 0);
                  d = si === k - 1 ? topRounded(x0, top, bw, Math.max(0, h), 3) : `M${x0} ${top}h${bw}v${Math.max(0, h)}h${-bw}Z`;
                  acc += val;
                } else {
                  const bx = x0 + si * bw + 1;
                  d = topRounded(bx, y(val), bw - 2, y(0) - y(val), 3);
                }
                return <path key={s.name} d={d} style={{ fill: colorOf(s, si) }} />;
              })}
              <rect
                x={m.l + band * i}
                y={m.t}
                width={band}
                height={ph}
                style={hover === i ? { fill: 'var(--surface-3)', fillOpacity: 0.35 } : { fill: 'transparent' }}
                onMouseMove={(ev) => showTip(i, ev)}
                onMouseLeave={() => {
                  setHover(null);
                  tip.hide();
                }}
              />
            </g>
          );
        })}
        {target && (
          <>
            <line className="target-line" x1={m.l} x2={W - m.r} y1={y(target.value)} y2={y(target.value)} />
            <text className="lbl halo" x={W - m.r} y={y(target.value) - 6} textAnchor="end">
              {target.label}
            </text>
          </>
        )}
      </svg>
      {tip.node}
    </div>
  );
}
